import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  aiGenerationReceived,
  clearPendingSearchView,
  setShowNLPFilterModal,
  setNLPModalState,
  setPendingSearchView,
  resetNLPModalState,
  restoreKeptNLPQuery,
  clearRecentSearches,
  userSubmittedPrompt,
  generateFiltersSuccessReceived,
  generateFiltersFailureReceived,
  NLP_STAGE,
} from 'actions/searchNLP';
import { getSearchView } from 'selectors/search';
import {
  getShowNLPFilterModal,
  getNLPModalState,
  getPendingSearchView,
  getRecentSearches,
  getPendingAiSessionId,
} from 'selectors/searchNLP';
import { usePost, useGet, useAuth } from 'hooks';
import {
  generateFilters,
  aiSearchHistoryLookup,
  aiSearchHistoryUse,
  aiRecentSearches,
  aiNlpSearchExamplesAll,
  aiNlpSearchExampleById,
  aiRecordSearchHistory,
} from 'services/api';
import {
  useNlpOrgContext,
  orgViewForEntityType,
  nlpSurfaceKey,
} from './NlpOrgContext';
import {
  ResearcherSearch,
  ActivitySearch,
} from 'components/Search/Results/helpers';
import { Icon } from 'components/common';
import { MultiStepModal } from 'components/common/MultiStepModal';
import { colours } from 'utils/theme';
import { aiSearchProductName } from 'utils/constants';
import {
  NLPStageOneModalView,
  toApiViewName,
  toCanonicalView,
} from './NLPStageOneModalView';
import { NLP_STAGE_ONE_MAX_QUERY_CHARS } from './NLPExpandingSearchInput';
import { keepQueryOnClose } from './keepQueryOnClose';
import { NLPStageTwoModalView } from './NLPStageTwoModalView';
import { NLPLicenseUpsell } from './NLPLicenseUpsell';
import { NLPFeedbackOpenButton } from './NLPFeedbackOpenButton';
import { NLPDemoCreditsIndicator } from './NLPDemoCreditsIndicator';
import {
  useAiSearchCredits,
  getDefaultSciSearchDailyLimitMessage,
  getDefaultSciSearchRateLimitMessage,
} from './useAiSearchCredits';
import {
  BetaBadge,
  H2,
  TitleContainer,
  TitleContent,
  TitleSpacer,
  DisclaimerLabel,
} from './styled';
import { isFullTrial } from 'utils/helpers/license';
import * as strings from './strings';
import { summariseOrgAiFilters } from 'components/Organisation/aiFilterAllowlist';

const buildRestoredAiSearchInterpretation = (
  savedPayload,
  interpretedQuery,
  explanation = 'Restored from your recent search'
) => {
  if (!savedPayload || typeof savedPayload !== 'object') {
    return null;
  }

  // New format: { filters, termSynonyms, termPlurals, termPluralSources? }
  if (
    savedPayload.filters &&
    typeof savedPayload.filters === 'object' &&
    !Array.isArray(savedPayload.filters)
  ) {
    const savedTermSynonyms =
      savedPayload.termSynonyms &&
      typeof savedPayload.termSynonyms === 'object' &&
      !Array.isArray(savedPayload.termSynonyms)
        ? savedPayload.termSynonyms
        : {};

    const savedTermPlurals =
      savedPayload.termPlurals &&
      typeof savedPayload.termPlurals === 'object' &&
      !Array.isArray(savedPayload.termPlurals)
        ? savedPayload.termPlurals
        : {};

    const savedTermPluralSources =
      savedPayload.termPluralSources &&
      typeof savedPayload.termPluralSources === 'object' &&
      !Array.isArray(savedPayload.termPluralSources)
        ? savedPayload.termPluralSources
        : undefined;

    return {
      filters: savedPayload.filters,
      termSynonyms: savedTermSynonyms,
      termPlurals: savedTermPlurals,
      ...(savedTermPluralSources
        ? { termPluralSources: savedTermPluralSources }
        : {}),
      explanation,
      interpretedQuery,
    };
  }

  // Legacy format: raw filters object only.
  return {
    filters: savedPayload,
    termSynonyms: {},
    termPlurals: {},
    explanation,
    interpretedQuery,
  };
};

/**
 * Build a lookup map from the all-examples index response.
 * Keys are lowercased prompts; values carry { id, viewName, category } for detail fetching.
 */
const buildAllExamplesIndex = allExamplesData => {
  const searches = allExamplesData?.searches || [];
  const map = new Map();
  for (const item of searches) {
    if (!item || typeof item !== 'object') continue;
    const prompt =
      typeof item.prompt === 'string'
        ? item.prompt
        : typeof item.Prompt === 'string'
        ? item.Prompt
        : null;
    const id = item.id ?? item.Id ?? null;
    const viewName = item.viewName ?? item.ViewName ?? null;
    const category = item.category ?? item.Category ?? null;
    if (prompt && id != null) {
      map.set(prompt.trim().toLowerCase(), { id, viewName, category });
    }
  }
  return map;
};

/**
 * Two-stage SciSearch AI modal.
 * Stage 1: search input with recent searches and example prompts.
 * Stage 2: debug view of raw filter response with Back/Confirm actions.
 */
export function NLPSearchModal() {
  const { user } = useAuth();
  const isDemo = isFullTrial(user);
  const dispatch = useDispatch();
  // Non-null only on the organisation profile. Swaps the generate/recents
  // endpoints and routes accept + main-search redirect rejections through
  // org-scoped handlers. Null on main search (unchanged).
  const orgCtx = useNlpOrgContext();
  const isOrgContext = Boolean(orgCtx);
  const surface = nlpSurfaceKey(orgCtx);
  const showModal = useSelector(getShowNLPFilterModal);
  const mainSearchView = useSelector(getSearchView);
  // The view the modal starts from: the profile tab on an org page (the
  // main-search view may be 'organisation' there, which the org endpoint rejects).
  const view = orgCtx
    ? orgViewForEntityType(orgCtx.entityType)
    : mainSearchView;
  const pendingSearchView = useSelector(getPendingSearchView);
  const recentSearches = useSelector(getRecentSearches);

  const {
    stage,
    query,
    skipSpellCheck,
    skipClassification,
    spellCheckPending,
    spellCheckOriginalQuery,
    ambiguityPending,
  } = useSelector(getNLPModalState);
  const pendingAiSessionId = useSelector(getPendingAiSessionId);

  const updateModalState = useCallback(
    updates => dispatch(setNLPModalState(updates)),
    [dispatch]
  );

  const currentView = useMemo(() => toApiViewName(view), [view]);
  const previousStageRef = useRef(stage);

  const [
    { res: recentData, loading: recentLoading, error: recentError },
    fetchRecent,
    setRecentRes,
  ] = useGet({
    url: orgCtx?.recentSearchesUrl ?? aiRecentSearches,
  });

  // Lifted so the section's open/closed state survives stage 1 ↔ stage 2 navigation
  // (stage 1 view unmounts on switch).
  const [recentSearchesCollapsed, setRecentSearchesCollapsed] = useState(true);
  const [{ res: allExamplesData }, fetchAllExamples] = useGet({
    url: aiNlpSearchExamplesAll,
  });

  // All-examples index: used for cross-view typed-prompt matching on search.
  const allExamplesIndex = useMemo(
    () => buildAllExamplesIndex(allExamplesData),
    [allExamplesData]
  );

  // View-filtered pills: derived client-side from the all-examples response.
  const suggestionsData = useMemo(() => {
    const allSearches = allExamplesData?.searches ?? allExamplesData?.Searches;
    if (!Array.isArray(allSearches)) return null;
    const forView = allSearches.filter(
      item =>
        (item.viewName ?? item.ViewName ?? '').toLowerCase() ===
        (currentView ?? '').toLowerCase()
    );
    return {
      exampleSearches: forView.filter(
        item => (item.category ?? item.Category) !== 'advanced'
      ),
      advancedUseCases: forView.filter(
        item => (item.category ?? item.Category) === 'advanced'
      ),
    };
  }, [allExamplesData, currentView]);

  useEffect(() => {
    if (!showModal) {
      return;
    }
    fetchRecent();
  }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showModal || isOrgContext) {
      // Example/advanced pills from the API are curated per main-search view. The
      // org profile shows static per-licence guidance from strings.js instead
      // (NLPOrgGuidanceQueries, SCIL-17611), so no fetch there.
      return;
    }
    fetchAllExamples();
  }, [showModal, isOrgContext]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const previousStage = previousStageRef.current;
    if (
      showModal &&
      previousStage === NLP_STAGE.REVIEW &&
      stage === NLP_STAGE.INPUT
    ) {
      // Refresh recents in the background when user returns from review to stage 1.
      fetchRecent();
    }
    previousStageRef.current = stage;
  }, [showModal, stage, fetchRecent]);

  // Existing filter generation hook
  const [
    { res: filterResponse, loading, error },
    generateFiltersRequest,
    setFilterResponse,
  ] = usePost({
    url: orgCtx?.filtersUrl ?? generateFilters,
    configuration: {
      onRequestSuccess: response => {
        dispatch(
          generateFiltersSuccessReceived({
            sessionId: pendingAiSessionId,
            response,
          })
        );
      },
      onRequestError: () => {
        dispatch(
          generateFiltersFailureReceived({ sessionId: pendingAiSessionId })
        );
      },
    },
  });

  // Daily/rate-limit credits (SCIL-14004). The chip renders only for demo users;
  // refresh is invoked after each /api/ai/filters attempt so the counter reflects reality.
  const {
    remaining: creditsRemaining,
    refresh: refreshCredits,
  } = useAiSearchCredits();

  useEffect(() => {
    if (!showModal) return;
    refreshCredits();
  }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showModal) return;
    dispatch(restoreKeptNLPQuery(surface));
  }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOutOfCredits =
    typeof creditsRemaining === 'number' && creditsRemaining <= 0;

  const closeModal = useCallback(
    ({ keepQuery }) => {
      dispatch(setShowNLPFilterModal(false));
      dispatch(resetNLPModalState({ keepQuery, surface }));
      dispatch(clearPendingSearchView());
      dispatch(clearRecentSearches());
    },
    [dispatch, surface]
  );

  // An unsearched query survives the X so the user can come back and rephrase it.
  const handleClose = useCallback(
    () => closeModal({ keepQuery: keepQueryOnClose({ stage, query }) }),
    [closeModal, stage, query]
  );

  useEffect(() => {
    if (!filterResponse) return;

    refreshCredits();

    // Redirect state only for main-search-servable rejections; the rest hit the error box below.
    if (isOrgContext && orgCtx.isMainSearchRedirectRejection(filterResponse)) {
      updateModalState({ apiError: null, orgMainSearchRedirect: 'irrelevant' });
      setFilterResponse(null);
      return;
    }

    if (filterResponse.errorCode) {
      updateModalState({
        apiError: {
          errorCode: filterResponse.errorCode,
          explanation: filterResponse.explanation,
          rejectionReason: filterResponse.rejectionReason ?? null,
          notFoundEntities: filterResponse.notFoundEntities ?? null,
          unsupportedFeatures: filterResponse.unsupportedFeatures ?? null,
          unsupportedNegations: filterResponse.unsupportedNegations ?? null,
        },
      });
      setFilterResponse(null);
      return;
    }

    updateModalState({ apiError: null });

    const hasSpellcheck =
      filterResponse.pendingAction === 'spell_correction' &&
      !!filterResponse.correctedQuery;
    // Orchestrator only populates isAmbiguous + suggestedEntityTypes when the
    // chooser should actually show — trust those fields directly.
    const hasEntityChooser =
      filterResponse.isAmbiguous &&
      filterResponse.suggestedEntityTypes?.length > 0;

    if (hasSpellcheck || hasEntityChooser) {
      updateModalState({
        spellCheckPending: hasSpellcheck,
        correctedQuery: hasSpellcheck ? filterResponse.correctedQuery : '',
        spellCheckOriginalQuery: hasSpellcheck ? query : '',
        ambiguityPending: hasEntityChooser,
        pendingSuggestedTypes: hasEntityChooser
          ? filterResponse.suggestedEntityTypes
          : null,
      });
      setFilterResponse(null);
      return;
    }

    // Confident classifier mismatch with the current view — silently redirect
    // using the top-1 suggestion. On the org profile this switches the tab.
    if (
      filterResponse.suggestedEntityTypes?.length > 0 &&
      !filterResponse.isAmbiguous
    ) {
      const classifiedView = toCanonicalView(
        filterResponse.suggestedEntityTypes[0]
      );
      const effectiveView = pendingSearchView || view;
      if (classifiedView && classifiedView !== effectiveView) {
        dispatch(setPendingSearchView(classifiedView));
      }
    }

    // An org profile supports a subset of the filters the AI can emit, so a
    // response can narrow only through filters this profile drops. Stop at
    // stage one with the query still editable rather than walking the user
    // through a review panel for filters that would all be dropped.
    if (
      isOrgContext &&
      summariseOrgAiFilters(filterResponse.filters).needsMainSearch
    ) {
      updateModalState({
        // The banner alone: this is a valid query in the wrong place, not a
        // failure, and the error box would add a second message telling them to
        // rephrase something that needs no rephrasing.
        apiError: null,
        orgMainSearchRedirect: 'unsupported-filters',
      });
      setFilterResponse(null);
      return;
    }

    // The session id was promoted into filters.aiFilterSessionId before the request fired, so it
    // matches the id this generation was logged under.
    dispatch(
      aiGenerationReceived({
        sessionId: pendingAiSessionId,
        query,
        interpretation: filterResponse,
      })
    );

    updateModalState({
      spellCheckPending: false,
      correctedQuery: '',
      spellCheckOriginalQuery: '',
      ambiguityPending: false,
      pendingSuggestedTypes: null,
      aiSearchInterpretation: filterResponse,
      stage: NLP_STAGE.REVIEW,
    });
    setFilterResponse(null);
  }, [
    pendingAiSessionId,
    dispatch,
    filterResponse,
    pendingSearchView,
    query,
    refreshCredits,
    setFilterResponse,
    updateModalState,
    view,
    isOrgContext,
    orgCtx,
  ]);

  // Handle API error
  useEffect(() => {
    if (!error) return;

    // Always resync credits so the chip/disabled state reflects Redis after the attempt.
    refreshCredits();

    const serverMessage = error.data?.title;
    const errorCodes = error.data?.errorCodes ?? [];
    const isDailyLimit = errorCodes.includes('AI.Search.Limit');
    const isRateLimit = errorCodes.includes('AI.Search.RateLimit');

    // Daily quota exhausted: SciSearchLimitBanner in stage 1 (not a toast) so the user can
    // still see the message while deciding what to do. Uses errorCodes (not HTTP status)
    // because both daily and rate limit share 412 to avoid the global 429 -> CAPTCHA flow.
    if (isDailyLimit) {
      updateModalState({
        apiError: {
          errorCode: 'daily_limit_reached',
          explanation: getDefaultSciSearchDailyLimitMessage(isDemo),
        },
      });
      return;
    }

    // Short-window rate limit: same inline SciSearchLimitBanner pattern as daily limit (input stays enabled).
    if (isRateLimit) {
      updateModalState({
        apiError: {
          errorCode: 'rate_limit_reached',
          explanation: serverMessage ?? getDefaultSciSearchRateLimitMessage(),
        },
      });
      return;
    }

    let errorMessage = 'Failed to generate search filters.';
    if (error.status === 500) {
      errorMessage =
        'An error occurred while generating filters. Please try again.';
    } else if (serverMessage) {
      errorMessage = serverMessage;
    }
    notification.error({
      message: 'Filter Generation Failed',
      description: errorMessage,
      duration: 5,
    });
  }, [error, isDemo, refreshCredits, updateModalState, user]);

  const fireAndForgetTouch = useCallback(id => {
    try {
      const token = localStorage.getItem('token');
      fetch(aiSearchHistoryUse(id), {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {
      // best-effort
    }
  }, []);

  const fireAndForgetRecordSearchHistory = useCallback(
    (
      naturalLanguageQuery,
      searchViewName,
      filterResponseJson,
      sourceType = null
    ) => {
      try {
        const token = localStorage.getItem('token');
        fetch(aiRecordSearchHistory, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            naturalLanguageQuery,
            viewName: searchViewName,
            filterResponseJson,
            sourceType,
          }),
        });
      } catch {
        // best-effort
      }
    },
    []
  );

  const onGenerateFiltersButtonClick = useCallback(
    async (queryString, viewOverride = null) => {
      const trimmedQuery = queryString.trim();
      if (!trimmedQuery) {
        return;
      }
      if (queryString.length > NLP_STAGE_ONE_MAX_QUERY_CHARS) {
        return;
      }

      updateModalState({ apiError: null, orgMainSearchRedirect: false });

      dispatch(
        userSubmittedPrompt({
          sessionId: pendingAiSessionId,
          queryString,
        })
      );

      // Resolve a canned example only when the trimmed input exactly matches a known example prompt.
      const resolvedExampleId =
        allExamplesIndex.get(trimmedQuery.toLowerCase())?.id ?? null;
      const resolvedExampleViewName =
        allExamplesIndex.get(trimmedQuery.toLowerCase())?.viewName ?? null;

      if (resolvedExampleId != null) {
        try {
          const token = localStorage.getItem('token');
          const detailRes = await fetch(
            aiNlpSearchExampleById(resolvedExampleId),
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
          if (detailRes.ok) {
            const detail = await detailRes.json();
            const restoredAiSearchInterpretation = buildRestoredAiSearchInterpretation(
              detail,
              trimmedQuery,
              'Restored from example search'
            );
            if (restoredAiSearchInterpretation) {
              dispatch(
                aiGenerationReceived({
                  // Restores skip the LLM call, so no id was promoted; key the record the same
                  // way confirm will resolve it (live id, else the pending id minted on open).
                  sessionId: pendingAiSessionId,
                  query: trimmedQuery,
                  interpretation: restoredAiSearchInterpretation,
                })
              );
              updateModalState({
                savedFilters: null,
                aiSearchInterpretation: restoredAiSearchInterpretation,
                stage: NLP_STAGE.REVIEW,
              });
              if (resolvedExampleViewName) {
                const canonicalView = toCanonicalView(resolvedExampleViewName);
                if (canonicalView) {
                  dispatch(setPendingSearchView(canonicalView));
                }
              }
              const effectiveSearchView =
                viewOverride || pendingSearchView || view;
              const fullJson = JSON.stringify(detail);
              const resolvedCategory = allExamplesIndex.get(
                trimmedQuery.toLowerCase()
              )?.category;
              fireAndForgetRecordSearchHistory(
                trimmedQuery,
                toApiViewName(effectiveSearchView),
                fullJson,
                resolvedCategory === 'advanced'
                  ? 'SciSearchAiFromUseCase'
                  : 'SciSearchAiFromExample'
              );
              return;
            }
          }
        } catch {
          // fall through to recent/history/AI paths
        }
        updateModalState({ savedFilters: null });
      }

      // Recent / pinned history: resolve a single history id, then emit at most one touch
      // (converges in-memory list + server lookup so we never double-count metrics).
      let resolvedRecentSearchId = null;
      const emitRecentSearchTouchOnce = () => {
        if (resolvedRecentSearchId == null || resolvedRecentSearchId === '') {
          return;
        }
        fireAndForgetTouch(resolvedRecentSearchId);
        resolvedRecentSearchId = null;
      };

      if (Array.isArray(recentSearches) && recentSearches.length > 0) {
        const normalizedQuery = trimmedQuery.toLowerCase();
        const matchingHistoryItem = recentSearches.find(
          item =>
            typeof item?.prompt === 'string' &&
            item.prompt.trim().toLowerCase() === normalizedQuery
        );

        if (
          matchingHistoryItem?.id != null ||
          matchingHistoryItem?.Id != null
        ) {
          resolvedRecentSearchId = String(
            matchingHistoryItem.id ?? matchingHistoryItem.Id
          );
        }

        // `savedFilters` is currently always null on /api/ai/recent-nlp-searches —
        // the backend nulls it out so SciSearch AI doesn't feel canned. This branch
        // is dormant today but kept so re-enabling canned recents would just be a
        // backend change.
        if (matchingHistoryItem?.savedFilters) {
          try {
            const restoredPayload = JSON.parse(
              matchingHistoryItem.savedFilters
            );
            const restoredAiSearchInterpretation = buildRestoredAiSearchInterpretation(
              restoredPayload,
              trimmedQuery
            );
            if (!restoredAiSearchInterpretation) {
              throw new Error('Unable to restore saved filters');
            }
            dispatch(
              aiGenerationReceived({
                sessionId: pendingAiSessionId,
                query: trimmedQuery,
                interpretation: restoredAiSearchInterpretation,
              })
            );
            updateModalState({
              savedFilters: null,
              aiSearchInterpretation: restoredAiSearchInterpretation,
              stage: NLP_STAGE.REVIEW,
            });
            emitRecentSearchTouchOnce();
            return;
          } catch {
            // fall through to server lookup
          }
        }
      }

      // Fall back to server lookup in case this query is not present in local state.
      // Skipped on the org profile: that lookup + its history endpoints are the
      // main-search global history; org recents are display-only (server /recent).
      const effectiveSearchView = viewOverride || pendingSearchView || view;
      const apiViewName =
        effectiveSearchView === 'activity'
          ? 'activity'
          : ResearcherSearch(effectiveSearchView)
          ? 'researcher'
          : 'organisation';
      try {
        if (isOrgContext) {
          throw new Error('skip global history lookup in org context');
        }
        const token = localStorage.getItem('token');
        const lookupUrl = `${aiSearchHistoryLookup}?prompt=${encodeURIComponent(
          trimmedQuery
        )}&currentView=${encodeURIComponent(apiViewName)}`;
        const lookupRes = await fetch(lookupUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (lookupRes.ok) {
          const lookupData = await lookupRes.json();
          const match = lookupData?.searches?.[0];
          if (
            (resolvedRecentSearchId == null || resolvedRecentSearchId === '') &&
            (match?.id != null || match?.Id != null)
          ) {
            resolvedRecentSearchId = String(match.id ?? match.Id);
          }
          // `savedFilters` is currently always null on /api/ai/search-history-lookup —
          // the backend nulls it out so SciSearch AI doesn't feel canned. This branch
          // is dormant today but kept so re-enabling canned recents would just be a
          // backend change.
          if (match?.savedFilters) {
            const restoredPayload = JSON.parse(match.savedFilters);
            const restoredAiSearchInterpretation = buildRestoredAiSearchInterpretation(
              restoredPayload,
              trimmedQuery
            );
            if (!restoredAiSearchInterpretation) {
              throw new Error('Unable to restore saved filters');
            }
            dispatch(
              aiGenerationReceived({
                sessionId: pendingAiSessionId,
                query: trimmedQuery,
                interpretation: restoredAiSearchInterpretation,
              })
            );
            updateModalState({
              savedFilters: null,
              aiSearchInterpretation: restoredAiSearchInterpretation,
              stage: NLP_STAGE.REVIEW,
            });
            emitRecentSearchTouchOnce();
            return;
          }
        }
      } catch {
        // fall through to AI generation
      }

      emitRecentSearchTouchOnce();

      // If no reusable history match is found, generate fresh filters with AI.
      // Must match portal_api.contracts.AI.EntityType (Activity = 3, not Organisation).
      // The org endpoint rejects Organisation (2), so on a profile anything that
      // is not an activity search is a People search.
      const entityType = ActivitySearch(effectiveSearchView)
        ? 3
        : isOrgContext || ResearcherSearch(effectiveSearchView)
        ? 1
        : 2;
      const hasUnchangedQueryAfterSuggestion =
        spellCheckPending &&
        spellCheckOriginalQuery?.trim() &&
        spellCheckOriginalQuery.trim() === trimmedQuery;
      // Skip spellcheck on chooser resolution paths too. The orchestrator's
      // spellcheck is an LLM call and not fully deterministic — re-running it
      // on the second submission can surface a typo it missed initially, which
      // would surprise the user with a spellcheck banner *after* they thought
      // stage 1 was resolved. Mirroring the classify-skip cases below keeps
      // the user on a single stage-1 resolution per query.
      const shouldSkipSpellCheck =
        hasUnchangedQueryAfterSuggestion ||
        skipSpellCheck ||
        ambiguityPending ||
        !!viewOverride;

      if (spellCheckPending) {
        updateModalState({
          spellCheckPending: false,
          correctedQuery: '',
          spellCheckOriginalQuery: '',
        });
      }

      // The pending session id travels with the generation request only; it is promoted into
      // filters.aiFilterSessionId when the user accepts the filters (stage-two confirm), so
      // abandoning the modal after a generation leaves no trace in the search filters.
      const requestBody = {
        naturalLanguageQuery: queryString,
        entityType: entityType,
        sessionId: pendingAiSessionId,
      };

      if (shouldSkipSpellCheck) {
        requestBody.skipSpellCheck = true;
      }

      // Skip classifier when:
      //  - viewOverride is passed (user picked a pill — classifier already ran), OR
      //  - ambiguityPending is set (user pressed Search while the chooser was showing,
      //    i.e. dismissed the suggestion and wants to keep their current view), OR
      //  - hasUnchangedQueryAfterSuggestion (user pressed Search while spellcheck was
      //    showing without changing the query — dismissed the spellcheck and we
      //    shouldn't reopen stage 1 with a fresh chooser), OR
      //  - skipClassification was previously set and not yet consumed.
      const effectiveSkipClassification =
        skipClassification ||
        ambiguityPending ||
        !!viewOverride ||
        hasUnchangedQueryAfterSuggestion;
      if (effectiveSkipClassification) {
        requestBody.skipClassification = true;
      }

      // Persist skip flags across re-fires of the same query — prevents
      // stage-1 prompts (spellcheck or chooser) reappearing in either
      // direction after the user has already resolved one. Flags are reset
      // when the query text changes (see setQuery in the stage-one view).
      const persistedUpdates = {};
      if (shouldSkipSpellCheck && !skipSpellCheck) {
        persistedUpdates.skipSpellCheck = true;
      }
      if (effectiveSkipClassification && !skipClassification) {
        persistedUpdates.skipClassification = true;
      }
      if (ambiguityPending) {
        persistedUpdates.ambiguityPending = false;
        persistedUpdates.pendingSuggestedTypes = null;
      }
      if (Object.keys(persistedUpdates).length > 0) {
        updateModalState(persistedUpdates);
      }

      generateFiltersRequest({
        body: requestBody,
      });
    },
    [
      view,
      pendingSearchView,
      spellCheckPending,
      spellCheckOriginalQuery,
      skipSpellCheck,
      skipClassification,
      ambiguityPending,
      pendingAiSessionId,
      recentSearches,
      allExamplesIndex,
      dispatch,
      updateModalState,
      generateFiltersRequest,
      fireAndForgetTouch,
      fireAndForgetRecordSearchHistory,
      isOrgContext,
    ]
  );

  if (!showModal) return null;

  const titleByStage = {
    [NLP_STAGE.INPUT]: aiSearchProductName,
    [NLP_STAGE.REVIEW]: 'Review and confirm filters',
  };

  const sharedTitle = (
    <TitleContainer>
      <TitleContent>
        <H2>
          {titleByStage[stage]}
          <BetaBadge>Beta</BetaBadge>
        </H2>
        <NLPDemoCreditsIndicator isDemo={isDemo} remaining={creditsRemaining} />
        <NLPLicenseUpsell />
      </TitleContent>
      <TitleSpacer />
      <NLPFeedbackOpenButton alwaysVisible />
    </TitleContainer>
  );

  const sharedBodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 250px)',
    overflow: 'hidden',
  };

  const footer = <DisclaimerLabel>{strings.disclaimer}</DisclaimerLabel>;

  // Org only: accepting the redirect banner opens main search in a new tab and closes here.
  const onMainSearchRedirect = redirectQuery => {
    orgCtx.onMainSearchRedirect(redirectQuery);
    // Handed off to main search, so there is nothing left to rephrase here.
    closeModal({ keepQuery: false });
  };

  const steps = {
    [NLP_STAGE.INPUT]: {
      title: sharedTitle,
      footer,
      bodyStyle: { ...sharedBodyStyle, gap: 12 },
      content: (
        <NLPStageOneModalView
          loading={loading}
          onSearchButtonClick={onGenerateFiltersButtonClick}
          onMainSearchRedirect={onMainSearchRedirect}
          recentData={recentData}
          recentLoading={recentLoading}
          recentError={recentError}
          setRecentListResponse={setRecentRes}
          suggestionsData={suggestionsData}
          isOutOfCredits={isOutOfCredits}
          isDemo={isDemo}
          recentSearchesCollapsed={recentSearchesCollapsed}
          onRecentSearchesCollapsedChange={setRecentSearchesCollapsed}
          orgCtx={orgCtx}
        />
      ),
    },
    [NLP_STAGE.REVIEW]: {
      title: sharedTitle,
      footer,
      bodyStyle: { ...sharedBodyStyle, gap: 25 },
      content: <NLPStageTwoModalView onClose={handleClose} />,
    },
  };

  return (
    <MultiStepModal
      open
      onCancel={handleClose}
      currentStep={stage}
      steps={steps}
      testId='nlp-search-modal'
      closeIcon={
        <Icon
          style={{ marginTop: 21 }}
          icon='close'
          size='lg'
          color={colours.grey}
        />
      }
    />
  );
}
